import { ActionIcon, Box, Group, Indicator, Text } from '@mantine/core';
import { openModal } from '@mantine/modals';
import { IconPaperclip } from '@tabler/icons-react';
import classNames from 'classnames/bind';
import { CopyToClipboard } from 'components/CopyToClipboard';
import { Panel } from 'components/Panel';
import { SuperButton } from 'components/SuperButton';
import dayjs from 'dayjs';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, useTranslation } from 'locales';
import { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { isEmpty, toUpperCase } from 'remeda';
import Amount from 'shared/components/Amount';
import ChooseCollaboratorModal from 'shared/components/ChooseCollaboratorModal';
import DownloadInvoice from 'shared/components/DownloadInvoice';
import { Icon, IconName } from 'shared/components/Icon';
import { creditNoteActions } from 'store/creditNote/creditnote.actions';
import { invoiceActions } from 'store/invoice/invoice.actions';
import { DialogShowId, DialogShowSize, dialogHide, showDialog } from 'store/view/view.actions';
import type { LightInvoice } from 'types/store/invoice-state';
import LinkDocument from '../LinkDocument';
import Invoice from '.';
import InvoiceDetailContext from './InvoiceDetail.context';
import { InvoicePdfModal } from './InvoicePdfModal';
import { UpdateDatesForm } from './SuperUserActions';
import styleIdentifiers from './ToHandleDetail.module.scss';

const styles = classNames.bind(styleIdentifiers);

type Props = {
  modal?: boolean;
};

export default function InvoiceInfo({ modal }: Props) {
  const { t } = useTranslation();
  const history = useHistory();
  const profile = useProfile();

  const { data, isCreditNote, noValidate, reloadInfo } = useContext(InvoiceDetailContext);

  const sendOriginalInvoice = () => {
    if (!isCreditNote) invoiceActions.detail({ id: data.id });
  };

  const openLinkDocument = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      title: t(i18nKeys.INVOICE.JOIN_FILE),
      children: <LinkDocument invoice={data} />,
    });
  };

  const importOriginalInvoice = () => {
    openModal({
      size: 'auto',
      title: t(i18nKeys.INVOICE.ORIGINAL_FILE),
      children: (
        <InvoicePdfModal
          isCreditNote={isCreditNote ?? false}
          onEditOriginalFile={sendOriginalInvoice}
          invoice={data}
        />
      ),
    });
  };

  const showInvoice = (linkedInvoice: LightInvoice) => {
    if (!modal) history.push(`/invoices/${linkedInvoice.id}`);
    else
      showDialog({
        id: DialogShowId.INVOICE,
        size: DialogShowSize.LARGE,
        children: <Invoice id={linkedInvoice.id} modal />,
      });
  };

  const editAccountManager = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.SMALL,
      title: t(i18nKeys.CHOOSE_ACCOUNT_MANAGER),
      children: (
        <ChooseCollaboratorModal
          initialValues={data.account_manager}
          onSubmit={(values: any) => {
            invoiceActions.assignToAccManager({
              id: data.id,
              data: values,
              callback: () => {
                (reloadInfo as any)();
                dialogHide(DialogShowId.CUSTOM);
              },
            });
          }}
        />
      ),
    });
  };

  const unlinkCn = (linkedInvoice: LightInvoice) => {
    creditNoteActions.unlinkToInvoices({
      id: data.id,
      data: {
        unlink_invoice: linkedInvoice.id,
      },
      callback: () => {
        dialogHide(DialogShowId.CUSTOM);
        (reloadInfo as any)();
      },
    });
  };

  const action = isCreditNote ? creditNoteActions : invoiceActions;
  const refreshInvoice = () => {
    action.info({ id: data.id, noReset: true, noLoading: true });
  };

  return (
    <Panel
      title={t(isCreditNote ? 'CREDIT_NOTE' : 'COMMON.INVOICE')}
      pb={0}
      className={styles('box')}
      withBorder
      topRight={
        <SuperButton
          modalContent={
            <UpdateDatesForm
              issueDate={data.issue_date.toString()}
              dueDate={data.due_date.toString()}
              invoiceId={data.id.toString()}
              refreshInvoice={refreshInvoice}
            />
          }
        >
          {t(i18nKeys.SUPERADMIN.UPDATE_DATES)}
        </SuperButton>
      }
    >
      <div>
        <Group gap={0}>
          <Box w={'50%'}>
            <Text size="xs" c="gray.5">
              {toUpperCase(t(i18nKeys.FORM.REFERENCE))}
            </Text>
          </Box>
          <Group gap={2} justify="center">
            <Text fw={600} c="purple.6">
              {data.reference}
            </Text>
            <CopyToClipboard text={data.reference} />
          </Group>
        </Group>
        {isCreditNote && !!data?.matched_payments_attributes.length && (
          <div className={styles('item')}>
            <span className={styles('subtitle')}>
              {t(
                data?.matched_payments_attributes.length > 1
                  ? i18nKeys.INVOICE.LINK_INVOICES
                  : i18nKeys.INVOICE.LINK_INVOICE,
              )}
            </span>
            <span
              className={styles('value', 'blue', 'clickable')}
              style={{ flexDirection: 'column' }}
            >
              {data.matched_payments_attributes.map(({ invoice_attributes }, i) => {
                const key = `${invoice_attributes.id}${i}`;
                return (
                  <span key={key} style={{ display: 'flex', alignItems: 'center' }}>
                    <span onClick={() => showInvoice(invoice_attributes)}>
                      {invoice_attributes.reference}
                    </span>
                    <Icon
                      name={IconName.UNLINK}
                      onClick={() => unlinkCn(invoice_attributes)}
                      className={styles('ml-10')}
                      title={t(i18nKeys.INVOICE.UNLINK)}
                    />
                  </span>
                );
              })}
            </span>
          </div>
        )}
        {data.order_reference && (
          <div className={styles('item')}>
            <span className={styles('subtitle')}>
              {t(i18nKeys.INVOICING.CREATE.ORDER_REFERENCE)}
            </span>
            <span className={styles('value')}>{data.order_reference}</span>
          </div>
        )}
        <div className={styles('item')}>
          <span className={styles('subtitle')}>{t(i18nKeys.ISSUE_DATE)}</span>
          <span className={styles('value', 'blue')}>
            {dayjs(data.issue_date.toISOString()).format('L')}
          </span>
        </div>
        <div className={styles('item')}>
          <span className={styles('subtitle')}>{t(i18nKeys.TOTAL_VAT_PRICE)}</span>
          <Amount
            localizedValue={data.total_tvac}
            value={data.localized_money_object.total_tvac}
            suffix={data.currency}
            className={styles('value')}
          />
        </div>
        <div className={styles('item')}>
          <span className={styles('subtitle')}>{t(i18nKeys.ACCOUNT_MANAGER)}</span>
          <div className={styles('value')}>
            {data.account_manager
              ? `${data.account_manager.first_name} ${data.account_manager.last_name}`
              : t(i18nKeys.NOT_ASSIGNED)}
            {profile?.isAdmin && (
              <Icon
                name={IconName.PENCIL}
                className={styles('ml-10')}
                onClick={editAccountManager}
              />
            )}
          </div>
        </div>
      </div>

      {!noValidate && (
        <div className={styles('card-footer')}>
          <DownloadInvoice
            importOriginalInvoice={importOriginalInvoice}
            invoice={data}
            hasUbl={data.has_ubl}
          />
          {data.additional_files_attributes && (
            <Indicator
              size={16}
              disabled={isEmpty(data.additional_files_attributes)}
              label={data.additional_files_attributes.length}
              offset={6}
              zIndex={10}
            >
              <ActionIcon onClick={openLinkDocument} variant="transparent" size="xl">
                <IconPaperclip stroke={1.5} size={28} />
              </ActionIcon>
            </Indicator>
          )}
        </div>
      )}
    </Panel>
  );
}
